import {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Checkbox,
  Form,
  FormContext,
  Modal,
  ModalContext,
  Toggle,
  toast,
  useServerResponseTranslation,
} from 'dash-ui';
import { useTranslation } from 'react-i18next';

import { CreateUserFormFields } from 'pages/management/constants/constants';
import { useUserFormValidation } from 'pages/management/hooks/useUserFormValidation';
import {
  GetCmsUsersDocument,
  useAddUserToCmsMutation,
  useUpdateUserCmsMutation,
} from 'gql/generated.graphql';

const SaveUserModal = () => {
  const { t } = useTranslation(['common', 'forms', 'pages']);
  const formRef = useRef<HTMLFormElement | null>(null);
  const { toggleModal, isOpenCurrent, payload, setPayload } =
    useContext(ModalContext);
  const { validate, isError } = useUserFormValidation();
  const [createUser] = useAddUserToCmsMutation({
    refetchQueries: [GetCmsUsersDocument],
  });
  const [updateUser] = useUpdateUserCmsMutation({
    refetchQueries: [GetCmsUsersDocument],
  });
  const translateServerError = useServerResponseTranslation();
  const {
    stateHandlers,
    formState,
    formErrors,
    formOnSubmit,
    resetForm,
    resetFormErrors,
    setFormValues,
  } = useContext(FormContext);

  const isOpen = isOpenCurrent['AddDashStaffUser'];
  const [isEdit, setIsEdit] = useState(false);
  const [enablePasswordChange, setEnablePasswordChange] = useState(false);

  useEffect(() => {
    const isEditMode = Boolean(payload);
    if (isEditMode) {
      setFormValues({
        firstName: payload?.user?.profile?.firstName || '',
        lastName: payload?.user?.profile?.lastName || '',
        profileId: payload?.user?.profile?._id || '',
        accountId: payload?.user?.account?._id || '',
        email: payload?.user?.account?.email || '',
        excludeFromReports: payload?.user?.account?.excludeFromReports || '',
        password: '',
      });
    } else {
      setFormValues({
        firstName: payload?.firstName || '',
        lastName: payload?.lastName || '',
        email: payload?.email || '',
        password: '',
      });
    }

    setIsEdit(isEditMode);
  }, [payload]);

  const closeModal = () => {
    toggleModal(false, 'AddDashStaffUser');
    setPayload(null);
    setEnablePasswordChange(false);
    resetForm();
    resetFormErrors();
  };

  const handleFieldsValues =
    (field: CreateUserFormFields) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      stateHandlers[field](value);
      validate(field, value);
    };

  const submitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();

    const {
      accountId,
      profileId,
      firstName,
      lastName,
      email,
      excludeFromReports,
      password,
    } = formState;

    if (isEdit) {
      updateUser({
        variables: {
          accountUpdateInput: {
            _id: accountId,
            password: password.length ? password : undefined,
            email,
            excludeFromReports,
          },
          profileInput: {
            _id: profileId,
            firstName,
            lastName,
          },
        },
        onCompleted: () => {
          closeModal();
          toast.success(t('pages:managementPage.toasts.userUpdated'));
        },
        onError: (error: any) => {
          toast.error(translateServerError(error));
        },
      });

      return;
    }
    createUser({
      variables: {
        accountInput: {
          password,
          email,
          excludeFromReports,
        },
        profileInput: {
          firstName,
          lastName,
        },
      },
      onCompleted: () => {
        closeModal();
        toast.success(t('pages:managementPage.toasts.userAdded'));
      },
      onError: (error: any) => {
        toast.error(translateServerError(error));
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      title={
        isEdit
          ? t('pages:managementPage.editUser').toString()
          : t('pages:managementPage.addNewUser')?.toString()
      }
      submitButton={{
        title: isEdit
          ? t('common:labels.update').toString()
          : t('common:labels.add').toString(),
        disabled: isError,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t('common:labels.cancel'),
        onClick: closeModal,
      }}
    >
      <Form onSubmit={submitForm} ref={formRef}>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t('forms:labels.firstName')}</Form.Label>
          <Form.Input
            placeholder={t('forms:placeholders.firstName') || ''}
            value={formState.firstName || ''}
            onChange={handleFieldsValues(CreateUserFormFields.FirstName)}
            $errorType={formErrors[CreateUserFormFields.FirstName]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[CreateUserFormFields.FirstName]?.type}
          >
            {formErrors[CreateUserFormFields.FirstName]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t('forms:labels.lastName')}</Form.Label>
          <Form.Input
            value={formState.lastName || ''}
            placeholder={t('forms:placeholders.lastName') || ''}
            onChange={handleFieldsValues(CreateUserFormFields.LastName)}
            $errorType={formErrors[CreateUserFormFields.LastName]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[CreateUserFormFields.LastName]?.type}
          >
            {formErrors[CreateUserFormFields.LastName]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t('forms:labels.email')}</Form.Label>
          <Form.Input
            value={formState.email || ''}
            placeholder={t('forms:placeholders.email') || ''}
            onChange={handleFieldsValues(CreateUserFormFields.Email)}
            $errorType={formErrors[CreateUserFormFields.Email]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[CreateUserFormFields.Email]?.type}
          >
            {formErrors[CreateUserFormFields.Email]?.message}
          </Form.Feedback>
        </Form.Group>
        {isEdit ? (
          <Checkbox
            mt={5}
            label={'Change Password'}
            checked={false}
            onChange={() => setEnablePasswordChange(!enablePasswordChange)}
          />
        ) : (
          <div />
        )}
        <Form.Group w="100%">
          <Form.Label>{t('forms:labels.password')}</Form.Label>
          <Form.InputPassword
            value={formState.password || ''}
            disabled={isEdit && !enablePasswordChange}
            onChange={handleFieldsValues(CreateUserFormFields.Password)}
            placeholder={
              isEdit ? '*******' : t('forms:placeholders.password') || ''
            }
            $errorType={formErrors[CreateUserFormFields.Password]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[CreateUserFormFields.Password]?.type}
          >
            {formErrors[CreateUserFormFields.Password]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t('forms:labels.excludeFromReports')}</Form.Label>
          <Toggle
            // @ts-ignore
            checked={formState.excludeFromReports}
            flexProps={{ mx: 2, my: 2 }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { checked } = e.target;
              stateHandlers[CreateUserFormFields.ExcludeFromReports](checked);
            }}
          />
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default SaveUserModal;
